import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useController, Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import updateLocale from 'dayjs/plugin/updateLocale'
import 'dayjs/locale/vi'
import SunIcons from '@/icons/SunIcons'
import MoonIcons from '@/icons/MoonIcons'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(updateLocale)
dayjs.locale('vi')

// Set week to start on Monday (1 = Monday, 0 = Sunday)
dayjs.updateLocale('vi', {
    weekStart: 1,
})

const getLocalTimezone = () => {
    return dayjs.tz.guess() || 'Asia/Ho_Chi_Minh'
}

const toLocalTime = (date: Dayjs | string | null) => {
    if (!date) return null
    return dayjs(date).tz(getLocalTimezone())
}

type DateTimePickerSize = 'small' | 'medium' | 'large'

interface DateTimePickerProps {
    name: string
    control: Control<any>
    label?: string
    placeholder?: string
    disabled?: boolean
    error?: string
    required?: boolean
    showClearButton?: boolean
    minDate?: Dayjs
    maxDate?: Dayjs
    size?: DateTimePickerSize
    includeTime?: boolean
    onChange?: (value: Dayjs | null) => void
}

const sizeClasses: Record<
    DateTimePickerSize,
    { input: string; icon: string; text: string; label: string }
> = {
    small: {
        input: 'px-4 py-1.5',
        icon: 'w-4 h-4 mr-2',
        text: 'text-xs',
        label: 'text-xs mb-1',
    },
    medium: {
        input: 'px-3 py-2',
        icon: 'w-4 h-4 mr-2',
        text: 'text-sm',
        label: 'text-sm mb-1.5',
    },
    large: {
        input: 'px-4 py-2.5',
        icon: 'w-5 h-5 mr-2.5',
        text: 'text-base',
        label: 'text-sm mb-1.5',
    },
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
    name,
    control,
    label,
    placeholder,
    disabled = false,
    error,
    required = false,
    showClearButton = true,
    minDate,
    maxDate,
    size = 'small',
    includeTime = true,
    onChange: onChangeCallback,
}) => {
    const sizeStyle = sizeClasses[size]
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const {
        field: { onChange, value },
        fieldState: { error: fieldError },
    } = useController({ name, control })

    const [isOpen, setIsOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
        toLocalTime(value)
    )
    const [tempDate, setTempDate] = useState<Dayjs | null>(
        toLocalTime(value) || toLocalTime(dayjs())
    )
    const [currentView, setCurrentView] = useState<'calendar' | 'time'>(
        'calendar'
    )
    const [yearMonthView, setYearMonthView] = useState<
        'none' | 'year' | 'month'
    >('none')
    const [clockMode, setClockMode] = useState<'hour' | 'minute'>('hour')
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        openAbove: false,
    })

    // Time input states
    const [hourInput, setHourInput] = useState('')
    const [minuteInput, setMinuteInput] = useState('')
    const [isEditingHour, setIsEditingHour] = useState(false)
    const [isEditingMinute, setIsEditingMinute] = useState(false)

    useEffect(() => {
        const localValue = toLocalTime(value)
        setSelectedDate(localValue)
        setTempDate(localValue || toLocalTime(dayjs()))
    }, [value])

    // Sync hour/minute input with tempDate
    useEffect(() => {
        if (tempDate && !isEditingHour) {
            const hour24 = tempDate.hour()
            const hour12 =
                hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
            setHourInput(hour12.toString().padStart(2, '0'))
        }
    }, [tempDate, isEditingHour])

    useEffect(() => {
        if (tempDate && !isEditingMinute) {
            setMinuteInput(tempDate.minute().toString().padStart(2, '0'))
        }
    }, [tempDate, isEditingMinute])

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            const clickedOutsideContainer =
                containerRef.current && !containerRef.current.contains(target)
            const clickedOutsideDropdown =
                dropdownRef.current && !dropdownRef.current.contains(target)

            if (clickedOutsideContainer && clickedOutsideDropdown) {
                handleCancel()
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    // Update dropdown position when opening
    const updatePosition = useCallback(() => {
        if (!isOpen || !inputRef.current) return

        const rect = inputRef.current.getBoundingClientRect()
        const VIEWPORT_PADDING = 16
        const DROPDOWN_WIDTH = 320 // Component uses w-[320px]
        const VERTICAL_SPACING = 8

        // Use actual dropdown height if available, fallback to approximate
        // Date-only mode is shorter (~400px) than datetime mode (~480px)
        const dropdownHeight =
            dropdownRef.current?.offsetHeight || (includeTime ? 480 : 400)

        // Calculate vertical positioning
        const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING
        const spaceAbove = rect.top - VIEWPORT_PADDING
        const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

        // Calculate horizontal positioning with overflow protection
        // Note: For fixed positioning, use viewport-relative coordinates (no scrollX/scrollY)
        let leftPosition = rect.left
        const spaceRight = window.innerWidth - rect.left - VIEWPORT_PADDING

        // Shift left if dropdown would overflow right edge
        if (spaceRight < DROPDOWN_WIDTH) {
            leftPosition = Math.max(
                VIEWPORT_PADDING,
                window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_PADDING
            )
        }

        // Ensure dropdown doesn't overflow left edge
        leftPosition = Math.max(VIEWPORT_PADDING, leftPosition)

        setDropdownPosition({
            top: openAbove
                ? rect.top - dropdownHeight - VERTICAL_SPACING
                : rect.bottom + VERTICAL_SPACING,
            left: leftPosition,
            openAbove,
        })
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return

        // Initial position update
        updatePosition()

        // Update position on scroll and resize
        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)

        return () => {
            window.removeEventListener('scroll', updatePosition, true)
            window.removeEventListener('resize', updatePosition)
        }
    }, [isOpen, updatePosition])

    const handleDateChange = (date: Dayjs) => {
        const updatedDate = tempDate
            ? toLocalTime(date.hour(tempDate.hour()).minute(tempDate.minute()))
            : toLocalTime(date.hour(9).minute(0))
        setTempDate(updatedDate)
        setYearMonthView('none')

        if (includeTime) {
            // Auto switch to time picker after selecting date
            setCurrentView('time')
            setClockMode('hour')
        } else {
            // Auto-confirm if time is not included
            setSelectedDate(updatedDate)
            onChange(updatedDate)
            onChangeCallback?.(updatedDate)
            setIsOpen(false)
            setCurrentView('calendar')
        }
    }

    const handleTimeChange = (hour: number, minute: number) => {
        if (tempDate) {
            const updatedDate = toLocalTime(
                tempDate.hour(hour).minute(minute).second(0)
            )
            setTempDate(updatedDate)
        }
    }

    const handleConfirm = () => {
        if (tempDate) {
            setSelectedDate(tempDate)
            onChange(tempDate)
            onChangeCallback?.(tempDate)
        }
        setIsOpen(false)
        setCurrentView('calendar')
        setYearMonthView('none')
        setClockMode('hour')
    }

    const handleCancel = () => {
        setTempDate(selectedDate || toLocalTime(dayjs()))
        setIsOpen(false)
        setCurrentView('calendar')
        setYearMonthView('none')
        setClockMode('hour')
    }

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedDate(null)
        setTempDate(toLocalTime(dayjs()))
        onChange(null)
        onChangeCallback?.(null)
    }

    const handleToday = () => {
        const today = toLocalTime(dayjs())!
        const updatedDate = tempDate
            ? today.hour(tempDate.hour()).minute(tempDate.minute())
            : today.hour(9).minute(0)
        setTempDate(updatedDate)
    }

    const generateCalendar = () => {
        const currentMonth = tempDate || toLocalTime(dayjs())!
        const startOfMonth = currentMonth.startOf('month')
        const endOfMonth = currentMonth.endOf('month')
        const startCalendar = startOfMonth.startOf('week')
        const endCalendar = endOfMonth.endOf('week')

        const days = []
        let currentDay = startCalendar

        while (
            currentDay.isBefore(endCalendar) ||
            currentDay.isSame(endCalendar, 'day')
        ) {
            days.push(currentDay)
            currentDay = currentDay.add(1, 'day')
        }

        return days
    }

    const isDateDisabled = useCallback(
        (date: Dayjs) => {
            if (minDate && date.isBefore(minDate, 'day')) return true
            if (maxDate && date.isAfter(maxDate, 'day')) return true
            return false
        },
        [minDate, maxDate]
    )

    const renderYearSelector = () => {
        const currentYear = (tempDate || toLocalTime(dayjs()))!.year()
        const years = Array.from({ length: 20 }, (_, i) => currentYear - 10 + i)

        return (
            <div className="p-3 grid grid-cols-4 gap-2 max-h-[280px] overflow-y-auto">
                {years?.map((year) => (
                    <button
                        key={year}
                        type="button"
                        onClick={() => {
                            setTempDate(tempDate!.year(year))
                            setYearMonthView('month')
                        }}
                        className={`
                            py-2 px-3 text-sm rounded-md transition-all duration-200
                            ${
                                year === currentYear
                                    ? 'bg-primary text-white font-medium shadow-sm'
                                    : 'hover:bg-gray-100 text-gray-700'
                            }
                        `}
                    >
                        {year}
                    </button>
                ))}
            </div>
        )
    }

    const renderMonthSelector = () => {
        const currentMonth = (tempDate || toLocalTime(dayjs()))!.month()
        const months = [
            'Th1',
            'Th2',
            'Th3',
            'Th4',
            'Th5',
            'Th6',
            'Th7',
            'Th8',
            'Th9',
            'Th10',
            'Th11',
            'Th12',
        ]

        return (
            <div className="p-3 grid grid-cols-3 gap-2">
                {months.map((month, idx) => (
                    <button
                        key={month}
                        type="button"
                        onClick={() => {
                            setTempDate(tempDate!.month(idx))
                            setYearMonthView('none')
                        }}
                        className={`
                            py-3 px-4 text-sm rounded-md transition-all duration-200
                            ${
                                idx === currentMonth
                                    ? 'bg-primary text-white font-medium shadow-sm'
                                    : 'hover:bg-gray-100 text-gray-700'
                            }
                        `}
                    >
                        {month}
                    </button>
                ))}
            </div>
        )
    }

    const renderCalendar = () => {
        if (yearMonthView === 'year') return renderYearSelector()
        if (yearMonthView === 'month') return renderMonthSelector()

        const days = generateCalendar()
        const currentMonth = tempDate || toLocalTime(dayjs())!
        // Week starts on Monday (T2)
        const weekdays = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']

        return (
            <div className="p-3">
                {/* Month/Year Navigation */}
                <div className="flex justify-between items-center mb-3">
                    <button
                        type="button"
                        onClick={() =>
                            setTempDate(currentMonth.subtract(1, 'month'))
                        }
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                    >
                        <svg
                            className="w-4 h-4 text-gray-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={() => setYearMonthView('year')}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors capitalize"
                    >
                        {currentMonth.format('MMMM, YYYY')}
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            setTempDate(currentMonth.add(1, 'month'))
                        }
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                    >
                        <svg
                            className="w-4 h-4 text-gray-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                    {weekdays.map((day, idx) => (
                        <div
                            key={day}
                            className={`text-center text-xs font-medium py-2 ${idx === 6 ? 'text-red-500' : 'text-gray-500'}`}
                        >
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                    {days.map((day, index) => {
                        const isCurrentMonth =
                            day.month() === currentMonth.month()
                        const isToday = day.isSame(toLocalTime(dayjs()), 'day')
                        const isSelected =
                            tempDate && day.isSame(tempDate, 'day')
                        const isSunday = day.day() === 0
                        const isDisabled = isDateDisabled(day)

                        return (
                            <button
                                key={index}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => handleDateChange(day)}
                                className={`
                                    relative w-9 h-9 flex items-center justify-center text-sm rounded-md transition-all duration-200
                                    ${!isCurrentMonth ? 'text-gray-300' : isSunday ? 'text-red-500' : 'text-gray-700'}
                                    ${isToday && !isSelected ? 'font-bold text-primary' : ''}
                                    ${isSelected ? 'bg-primary text-white shadow-sm' : 'hover:bg-gray-100'}
                                    ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                                `}
                            >
                                {day.date()}
                                {isToday && !isSelected && (
                                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
                                )}
                            </button>
                        )
                    })}
                </div>

                {/* Today Button */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleToday}
                        className="w-full py-2 text-sm text-primary hover:bg-primary/5 rounded-md transition-colors font-medium"
                    >
                        {t('datetime-picker-today')}
                    </button>
                </div>
            </div>
        )
    }

    const renderTimePicker = () => {
        const currentTime = tempDate || toLocalTime(dayjs())!
        const hour24 = currentTime.hour()
        const minute = currentTime.minute()
        const isAM = hour24 < 12
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24

        // Clock configuration
        const clockSize = 200
        const clockCenter = clockSize / 2
        const numberRadius = 70

        // Get clock numbers based on mode
        const clockNumbers =
            clockMode === 'hour'
                ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
                : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]

        // Calculate hand angle
        const getHandAngle = () => {
            if (clockMode === 'hour') {
                return (hour12 % 12) * 30 - 90 // 30 degrees per hour
            } else {
                return (minute / 60) * 360 - 90 // 6 degrees per minute
            }
        }

        // Get selected value for highlighting
        const getSelectedValue = () => {
            if (clockMode === 'hour') {
                return hour12 === 12 ? 12 : hour12
            } else {
                return minute
            }
        }

        // Handle clock number click
        const handleClockClick = (value: number) => {
            if (clockMode === 'hour') {
                let newHour24 = value
                if (value === 12) {
                    newHour24 = isAM ? 0 : 12
                } else {
                    newHour24 = isAM ? value : value + 12
                }
                handleTimeChange(newHour24, minute)
                setClockMode('minute') // Auto switch to minute
            } else {
                handleTimeChange(hour24, value)
            }
        }

        // Handle AM/PM toggle
        const handleAmPmToggle = (newIsAM: boolean) => {
            if (newIsAM === isAM) return
            const newHour = newIsAM ? hour24 - 12 : hour24 + 12
            handleTimeChange(Math.max(0, Math.min(23, newHour)), minute)
        }

        const handAngle = getHandAngle()
        const selectedValue = getSelectedValue()

        // Handle hour input blur - apply the value
        const handleHourBlur = () => {
            setIsEditingHour(false)
            const val = hourInput.replace(/\D/g, '')
            if (val === '') {
                setHourInput(hour12.toString().padStart(2, '0'))
                return
            }
            let num = parseInt(val, 10)
            if (num > 12) num = 12
            if (num < 1) num = 1
            // Convert 12-hour to 24-hour
            let newHour24 = num
            if (num === 12) {
                newHour24 = isAM ? 0 : 12
            } else {
                newHour24 = isAM ? num : num + 12
            }
            handleTimeChange(newHour24, minute)
        }

        // Handle minute input blur - apply the value
        const handleMinuteBlur = () => {
            setIsEditingMinute(false)
            const val = minuteInput.replace(/\D/g, '')
            if (val === '') {
                setMinuteInput(minute.toString().padStart(2, '0'))
                return
            }
            let num = parseInt(val, 10)
            if (num > 59) num = 59
            if (num < 0) num = 0
            handleTimeChange(hour24, num)
        }

        // Handle Enter key
        const handleKeyDown = (
            e: React.KeyboardEvent,
            type: 'hour' | 'minute'
        ) => {
            if (e.key === 'Enter') {
                if (type === 'hour') handleHourBlur()
                else handleMinuteBlur()
                ;(e.target as HTMLInputElement).blur()
            }
        }

        return (
            <div className="p-4">
                {/* Digital Time Display */}
                <div className="flex items-center justify-center gap-4 mb-4">
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={2}
                        value={hourInput}
                        onChange={(e) =>
                            setHourInput(
                                e.target.value.replace(/\D/g, '').slice(0, 2)
                            )
                        }
                        onFocus={(e) => {
                            setClockMode('hour')
                            setIsEditingHour(true)
                            e.target.select()
                        }}
                        onBlur={handleHourBlur}
                        onKeyDown={(e) => handleKeyDown(e, 'hour')}
                        className={`
                            w-16 h-10 text-2xl font-medium rounded-md border border-gray-200 transition-all text-center outline-none
                            ${
                                clockMode === 'hour'
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-gray-200 text-gray-700 hover:border-gray-300'
                            }
                        `}
                    />
                    <span className="text-2xl font-medium text-gray-400">
                        :
                    </span>
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={2}
                        value={minuteInput}
                        onChange={(e) =>
                            setMinuteInput(
                                e.target.value.replace(/\D/g, '').slice(0, 2)
                            )
                        }
                        onFocus={(e) => {
                            setClockMode('minute')
                            setIsEditingMinute(true)
                            e.target.select()
                        }}
                        onBlur={handleMinuteBlur}
                        onKeyDown={(e) => handleKeyDown(e, 'minute')}
                        className={`
                            w-16 h-10 text-2xl font-medium rounded-md border border-gray-200 transition-all text-center outline-none
                            ${
                                clockMode === 'minute'
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-gray-200 text-gray-700 hover:border-gray-300'
                            }
                        `}
                    />

                    {/* AM/PM Toggle */}
                    <div className="flex flex-col gap-0.5 ml-3">
                        <button
                            type="button"
                            onClick={() => handleAmPmToggle(true)}
                            className={`
                                flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all
                                ${
                                    isAM
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                                }
                            `}
                        >
                            <SunIcons className="w-3.5 h-3.5" />
                            AM
                        </button>
                        <button
                            type="button"
                            onClick={() => handleAmPmToggle(false)}
                            className={`
                                flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all
                                ${
                                    !isAM
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                                }
                            `}
                        >
                            <MoonIcons className="w-3.5 h-3.5" />
                            PM
                        </button>
                    </div>
                </div>

                {/* Analog Clock */}
                <div className="flex justify-center">
                    <div
                        className="relative rounded-full bg-gray-50 border border-gray-200"
                        style={{ width: clockSize, height: clockSize }}
                    >
                        {/* Clock Hand */}
                        <div
                            className="absolute top-1/2 left-1/2 origin-left bg-primary rounded-full transition-transform duration-200"
                            style={{
                                width: numberRadius - 16,
                                height: 2,
                                transform: `translate(0, -50%) rotate(${handAngle}deg)`,
                            }}
                        />

                        {/* Center Dot */}
                        <div
                            className="absolute w-3 h-3 bg-primary rounded-full"
                            style={{
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                            }}
                        />

                        {/* Clock Numbers */}
                        {clockNumbers.map((num, idx) => {
                            const angle = (idx * 30 - 90) * (Math.PI / 180)
                            const x =
                                clockCenter + numberRadius * Math.cos(angle)
                            const y =
                                clockCenter + numberRadius * Math.sin(angle)
                            const isSelected = num === selectedValue

                            return (
                                <button
                                    key={num}
                                    type="button"
                                    onClick={() => handleClockClick(num)}
                                    className={`
                                        absolute w-9 h-9 flex items-center justify-center text-sm font-medium
                                        rounded-full transition-all duration-200
                                        ${
                                            isSelected
                                                ? 'bg-primary text-white'
                                                : 'text-gray-700 hover:bg-gray-200'
                                        }
                                    `}
                                    style={{
                                        left: x,
                                        top: y,
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    {num.toString().padStart(2, '0')}
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* Mode indicator */}
                <div className="text-center mt-3 text-xs text-gray-500">
                    {clockMode === 'hour'
                        ? t('datetime-picker-select-hour')
                        : t('datetime-picker-select-minute')}
                </div>
            </div>
        )
    }

    const displayError = error || fieldError?.message

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label
                    className={`block font-normal text-gray-700 ${sizeStyle.label}`}
                >
                    {label}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}

            <div
                ref={inputRef}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`
                    group flex items-center border rounded-[4px] cursor-pointer transition-all duration-200
                    ${sizeStyle.input}
                    ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'bg-white hover:border-gray-400'}
                    ${displayError ? 'border-red-400 focus-within:ring-2 focus-within:ring-red-100' : 'border-gray-300'}
                    ${isOpen ? 'border-primary ring-2 ring-primary/20' : ''}
                `}
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={`${sizeStyle.icon} text-gray-400 flex-shrink-0`}
                >
                    <path
                        d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M15.695 13.7H15.704M15.695 16.7H15.704M11.995 13.7H12.005M11.995 16.7H12.005M8.295 13.7H8.304M8.295 16.7H8.304"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>

                <div className="flex-1 min-w-0">
                    {selectedDate ? (
                        <span className={`${sizeStyle.text} text-gray-800`}>
                            {selectedDate.format('DD/MM/YYYY')}
                            {includeTime && (
                                <>
                                    <span className="text-gray-400 mx-1">
                                        ·
                                    </span>
                                    <span className="text-primary">
                                        {selectedDate.format('HH:mm')}
                                    </span>
                                </>
                            )}
                        </span>
                    ) : (
                        <span className={`${sizeStyle.text} text-gray-400`}>
                            {placeholder !== undefined
                                ? placeholder
                                : t('datetime-picker-placeholder')}
                        </span>
                    )}
                </div>

                {selectedDate && showClearButton && !disabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                    >
                        <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                )}

                <svg
                    className={`w-4 h-4 text-gray-400 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </div>

            {displayError && (
                <p
                    className={`mt-1 ${sizeStyle.text} text-red-500 flex items-center gap-1`}
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {displayError}
                </p>
            )}

            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl w-[320px] overflow-hidden animate-in fade-in duration-200 ${
                            dropdownPosition.openAbove
                                ? 'slide-in-from-bottom-2'
                                : 'slide-in-from-top-2'
                        }`}
                        style={{
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                        }}
                    >
                        {/* Tabs */}
                        {includeTime && (
                            <div className="flex bg-gray-50 p-1 m-2 rounded-md">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCurrentView('calendar')
                                        setYearMonthView('none')
                                    }}
                                    className={`
                                    flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-md transition-all duration-200
                                    ${
                                        currentView === 'calendar'
                                            ? 'bg-white text-gray-800 shadow-sm font-medium'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }
                                `}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    {t('datetime-picker-date-tab')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentView('time')}
                                    className={`
                                    flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-md transition-all duration-200
                                    ${
                                        currentView === 'time'
                                            ? 'bg-white text-gray-800 shadow-sm font-medium'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }
                                `}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    {t('datetime-picker-time-tab')}
                                </button>
                            </div>
                        )}

                        {/* Content */}
                        <div className={includeTime ? 'min-h-[300px]' : ''}>
                            {currentView === 'calendar'
                                ? renderCalendar()
                                : renderTimePicker()}
                        </div>

                        {/* Footer */}
                        {includeTime && (
                            <div className="flex items-center justify-between gap-2 p-3 border-t border-gray-100 bg-gray-50">
                                <div className="text-xs text-gray-500">
                                    {tempDate?.format('DD/MM/YYYY HH:mm')}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                                    >
                                        {t('datetime-picker-cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirm}
                                        className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary/90 rounded-md transition-colors font-medium shadow-sm"
                                    >
                                        {t('datetime-picker-confirm')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>,
                    document.body
                )}
        </div>
    )
}

export default DateTimePicker
